import { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { useNavigate } from "react-router";
import {
  AlertTriangle,
  Upload,
  X,
  Bug,
  Lightbulb,
  Wrench,
  MessageSquare,
  FileText,
  Film,
  Image as ImageIcon,
  ChevronRight,
} from "lucide-react";
import {
  ALERT_BANNER_SURFACE_CLASS,
  Button,
  FieldError,
  Input,
  Label,
  Textarea,
  ToggleGroup,
  TruncatedText,
  fieldErrorId,
  iconTileToneClass,
} from "@/components/ui";
import { FileDropzone } from "@/components/common";
import { FILE_ACCEPT } from "@/constants/fileAccept";
import {
  FILE_VALIDATION_PRESETS,
  formatFileSizeLimit,
  partitionValidatedFiles,
  toastRejectedFiles,
} from "@/lib/fileValidation";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";
import { TicketPriority } from "@/components/tickets/config";
import {
  supportTicketService,
  type TicketCategory,
  type TicketPriority as TicketPriorityValue,
} from "@/services/supportTicketService";
import { useStorageUploadBatch } from "@/hooks/useStorageUploadBatch";
import { StorageUploadProgressBanner } from "@/components/common/StorageUploadProgressBanner";

// Ticket categories (labels from i18n)
const ticketCategoryDefs = [
  {
    id: "problem" as const,
    icon: Bug,
    tone: "error",
  },
  {
    id: "suggestion" as const,
    icon: Lightbulb,
    tone: "warning",
  },
  {
    id: "improvement" as const,
    icon: Wrench,
    tone: "accent",
  },
  {
    id: "question" as const,
    icon: MessageSquare,
    tone: "primary",
  },
  {
    id: "billing" as const,
    icon: FileText,
    tone: "success",
  },
] as const;

const PRIORITY_KEYS = ["critical", "high", "medium", "low"] as const;

const adminPriorityDots = [
  { value: TicketPriority.CRITICAL, dot: "bg-error-500" },
  { value: TicketPriority.HIGH, dot: "bg-orange-500" },
  { value: TicketPriority.MEDIUM, dot: "bg-warning-500" },
  { value: TicketPriority.LOW, dot: "bg-primary-400" },
] as const;

/** Partner-facing labels map to internal priority on submit. */
const partnerPriorityOptions = [
  {
    value: TicketPriority.LOW,
    dot: "bg-primary-400",
    labelKey: "normal" as const,
  },
  {
    value: TicketPriority.MEDIUM,
    dot: "bg-warning-500",
    labelKey: "high" as const,
  },
] as const;

interface AttachedFile {
  file: File;
  type: "image" | "video" | "document";
}

const CATEGORY_SECTION_ID = "support-ticket-category";

export function SupportTicketForm({
  initialSubject,
}: {
  /** Subject prefilled from the page that opened the form (empty when none). */
  initialSubject: string;
}) {
  const { t } = useTranslation("support");
  const { user, hasPermission } = useAuthStore();
  const adminPriorityUi = hasPermission(Permission.SUPPORT_TICKETS_UPDATE_ANY);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const categorySectionRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const categoryErrorId = fieldErrorId(CATEGORY_SECTION_ID);

  const ticketCategories = useMemo(
    () =>
      ticketCategoryDefs.map((cat) => ({
        ...cat,
        label: t(`ticketForm.categories.${cat.id}`),
      })),
    [t],
  );

  const adminPriorityLevels = useMemo(
    () =>
      adminPriorityDots.map((p) => ({
        value: p.value,
        dot: p.dot,
        label: t(
          `ticketForm.priorities.${PRIORITY_KEYS[p.value - 1]}`,
        ),
      })),
    [t],
  );

  const partnerPriorityLevels = useMemo(
    () =>
      partnerPriorityOptions.map((p) => ({
        value: p.value,
        dot: p.dot,
        label: t(`ticketForm.priorities.${p.labelKey}`),
      })),
    [t],
  );

  const priorityLevels = adminPriorityUi
    ? adminPriorityLevels
    : partnerPriorityLevels;

  const defaultPriority = adminPriorityUi
    ? TicketPriority.MEDIUM
    : TicketPriority.LOW;

  const [category, setCategory] = useState<TicketCategory | null>(null);
  const [categoryError, setCategoryError] = useState(false);
  const [priority, setPriority] =
    useState<TicketPriorityValue>(defaultPriority);
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<AttachedFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { activeUpload, uploadTicketAttachments } = useStorageUploadBatch();

  const selectCategory = (next: TicketCategory) => {
    setCategory(next);
    setCategoryError(false);
  };

  const handleFileSelect = (files: File[]) => {
    const { accepted, rejected } = partitionValidatedFiles(
      files,
      FILE_VALIDATION_PRESETS.supportAttachment,
    );

    toastRejectedFiles(rejected, {
      tooLarge: (file, maxSize) =>
        t("ticketForm.fileTooLarge", {
          name: file.name,
          max: formatFileSizeLimit(maxSize),
        }),
      unsupported: (file) =>
        t("ticketForm.fileUnsupported", { name: file.name }),
    });

    const newAttachments: AttachedFile[] = accepted.map(({ file, kind }) => ({
      file,
      type:
        kind === "image" ? "image" : kind === "video" ? "video" : "document",
    }));

    if (newAttachments.length === 0) {
      return;
    }

    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => {
      const newAttachments = [...prev];
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  };

  const resetForm = () => {
    setCategory(null);
    setCategoryError(false);
    setPriority(defaultPriority);
    setSubject("");
    setMessage("");
    setAttachments([]);
  };

  /** Never throws: the ticket already exists, so a failure here must not look like a retryable submit. */
  const attachFilesToOpeningMessage = async (
    ticketId: string,
    files: File[],
  ): Promise<boolean> => {
    try {
      const uploadedAttachments = await uploadTicketAttachments(
        ticketId,
        files,
      );
      await supportTicketService.attachToOpeningMessage(
        ticketId,
        uploadedAttachments,
      );
      return true;
    } catch (error) {
      console.error("Failed to attach files to ticket:", error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!category) {
      setCategoryError(true);
      categorySectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (!subject.trim()) {
      toast.error(t("ticketForm.toastEnterSubject"));
      return;
    }
    if (!message.trim()) {
      toast.error(t("ticketForm.toastEnterMessage"));
      return;
    }

    setIsSubmitting(true);

    try {
      const ticket = await supportTicketService.createTicket({
        category,
        priority,
        subject: subject.trim(),
        content: message.trim(),
      });

      const files = attachments.map((attachment) => attachment.file);
      const attached =
        files.length === 0 ||
        (await attachFilesToOpeningMessage(ticket.id, files));
      if (attached) {
        toast.success(t("ticketForm.toastSubmitted"));
      } else {
        toast(t("ticketForm.toastAttachmentsFailed"), {
          icon: (
            <AlertTriangle className="h-5 w-5 shrink-0 text-warning-500" />
          ),
          className: cn(ALERT_BANNER_SURFACE_CLASS.warning, "text-warning-950"),
        });
      }
      resetForm();
      navigate(`/support/tickets/${ticket.id}`);
    } catch (error) {
      console.error("Failed to submit ticket:", error);
      toastApiError(error, "generic", t("ticketForm.toastSubmitFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <section
        ref={categorySectionRef}
        id={CATEGORY_SECTION_ID}
        aria-labelledby={`${CATEGORY_SECTION_ID}-label`}
        aria-invalid={categoryError ? "true" : undefined}
        aria-describedby={categoryError ? categoryErrorId : undefined}
      >
        <h4
          id={`${CATEGORY_SECTION_ID}-label`}
          className={cn(
            "text-[15px] font-semibold tracking-tight",
            categoryError ? "text-error-700" : "text-surface-900",
          )}
        >
          {t("ticketForm.requestTypeLabel")}
        </h4>
        <p className="mt-0.5 mb-3 text-[13px] text-surface-500">
          {t("ticketForm.requestTypeHint")}
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {ticketCategories.map((cat) => {
            const Icon = cat.icon;
            const isSelected = category === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => selectCategory(cat.id)}
                aria-pressed={isSelected}
                className={cn(
                  "flex flex-col items-center gap-2.5 rounded-lg border px-3 py-4 shadow-xs transition-all duration-200",
                  isSelected
                    ? "border-primary-500 bg-primary-50/60 ring-1 ring-primary-500"
                    : categoryError
                      ? "border-error-500 bg-error-50/40 ring-1 ring-error-500/40 hover:border-error-600"
                      : "border-surface-200 bg-surface-0 hover:-translate-y-px hover:border-surface-300 hover:shadow-elevated",
                )}
              >
                <div
                  className={cn(
                    "rounded-lg p-2.5 ring-1 ring-inset",
                    iconTileToneClass(cat.tone),
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <span
                  className={cn(
                    "text-sm font-medium",
                    isSelected
                      ? "text-primary-700"
                      : categoryError
                        ? "text-error-700"
                        : "text-surface-600",
                  )}
                >
                  {cat.label}
                </span>
              </button>
            );
          })}
        </div>
        {categoryError ? (
          <FieldError id={categoryErrorId}>
            {t("ticketForm.toastSelectCategory")}
          </FieldError>
        ) : null}
      </section>

      <section className="border-t border-surface-200/70 pt-6">
        <h4 className="text-[15px] font-semibold tracking-tight text-surface-900">
          {t("ticketForm.priorityLabel")}
        </h4>
        <p className="mt-0.5 mb-3 text-[13px] text-surface-500">
          {t("ticketForm.priorityHint")}
        </p>
        <ToggleGroup
          value={String(priority)}
          onChange={(value) =>
            setPriority(Number(value) as TicketPriorityValue)
          }
          ariaLabel={t("ticketForm.priorityLabel")}
          options={priorityLevels.map((level) => ({
            value: String(level.value),
            label: (
              <span className="inline-flex items-center gap-1.5">
                <span
                  className={cn("h-2 w-2 shrink-0 rounded-full", level.dot)}
                  aria-hidden
                />
                {level.label}
              </span>
            ),
          }))}
        />
      </section>

      <section className="space-y-4 rounded-lg border border-surface-200 bg-surface-50 px-4 py-4 dark:bg-surface-100/40">
        <div>
          <Label htmlFor="support-ticket-subject" emphasis="semibold" spacing="relaxed">
            {t("ticketForm.subjectLabel")}
          </Label>
          <Input
            id="support-ticket-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t("ticketForm.subjectPlaceholder")}
            maxLength={200}
            className="bg-surface-0"
          />
        </div>

        <div>
          <Label htmlFor="support-ticket-message" emphasis="semibold" spacing="relaxed">
            {t("ticketForm.messageLabel")}
          </Label>
          <Textarea
            id="support-ticket-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("ticketForm.messagePlaceholder")}
            rows={6}
            className="resize-none bg-surface-0"
          />
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between gap-3">
            <Label htmlFor="support-ticket-attachments" emphasis="semibold" spacing="none">
              {t("ticketForm.attachmentsLabel")}
            </Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              leftIcon={<Upload className="h-4 w-4" />}
            >
              {t("ticketForm.addFiles")}
            </Button>
          </div>
          <FileDropzone
            id="support-ticket-attachments"
            multiple
            accept={FILE_ACCEPT.supportAttachment}
            visible={attachments.length === 0}
            inputRef={fileInputRef}
            className="gap-2 border-surface-200 bg-surface-0 px-4 py-6 hover:border-primary-300 hover:bg-primary-50/30 hover:text-primary-700"
            iconClassName="h-5 w-5"
            title={t("ticketForm.attachmentsHint")}
            titleClassName="font-normal text-surface-500"
            onFilesSelected={handleFileSelect}
          />

          {attachments.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {attachments.map((attachment, index) => (
                <div
                  key={index}
                  className="group relative flex items-center gap-2 rounded-lg border border-surface-200 bg-surface-0 px-3 py-2"
                >
                  {attachment.type === "image" ? (
                    <ImageIcon className="h-4 w-4 text-primary-500" />
                  ) : attachment.type === "video" ? (
                    <Film className="h-4 w-4 text-accent-500" />
                  ) : (
                    <FileText className="h-4 w-4 text-surface-500" />
                  )}
                  <TruncatedText className="max-w-[140px] text-sm text-surface-700">
                    {attachment.file.name}
                  </TruncatedText>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    className="text-surface-400 hover:text-error-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {activeUpload ? (
            <StorageUploadProgressBanner upload={activeUpload} />
          ) : null}
        </div>
      </section>

      <div className="-mx-5 -mb-4 rounded-b-xl border-t border-surface-200/70 bg-surface-50 px-5 py-4 dark:bg-surface-100/30">
        <p className="mb-3 text-center text-[13px] text-surface-500">
          {t("ticketForm.respondLine", { email: user?.email ?? "" })}
        </p>
        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting}
          isLoading={isSubmitting}
          rightIcon={
            !isSubmitting ? <ChevronRight className="h-4 w-4" /> : undefined
          }
        >
          {t("ticketForm.submit")}
        </Button>
      </div>
    </form>
  );
}
